import type { PublicSnapshot } from "@paddock-signal/contracts";

export interface FormattedSessionTime {
  viewer: string;
  viewerClock: string;
  viewerOffset: string;
  track: string;
  trackOffset: string;
}

type RaceWeekend = NonNullable<PublicSnapshot["weekend"]>;
export type RaceSession = RaceWeekend["sessions"][number];

export type SessionDisplayStatus =
  | "completed"
  | "scheduled"
  | "next"
  | "ongoing"
  | "delayed"
  | "cancelled";

/**
 * Reconciles a published session with the viewer's current clock.
 *
 * Session status is derived by the refresh worker at publication time, so a
 * stale-but-usable snapshot can otherwise keep a session marked scheduled
 * after it has finished. This function is deliberately conservative: delayed
 * is only retained when a provider/override has explicitly supplied it;
 * timestamps alone cannot prove a delay.
 */
export function sessionDisplayStatus(
  session: RaceSession,
  sessions: readonly RaceSession[],
  now = new Date(),
): SessionDisplayStatus {
  if (session.status === "cancelled") return "cancelled";
  if (session.status === "delayed") return "delayed";
  const startsAt = Date.parse(session.startsAt);
  const endsAt = Date.parse(session.endsAt);
  const nowMs = now.getTime();
  if (
    session.status === "completed" ||
    (Number.isFinite(endsAt) && endsAt <= nowMs)
  ) {
    return "completed";
  }
  if (
    session.status === "live" ||
    (Number.isFinite(startsAt) &&
      startsAt <= nowMs &&
      (!Number.isFinite(endsAt) || endsAt > nowMs))
  ) {
    return "ongoing";
  }
  const next = [...sessions]
    .filter((candidate) => {
      if (candidate.status === "cancelled") return false;
      const candidateEnd = Date.parse(candidate.endsAt);
      return !Number.isFinite(candidateEnd) || candidateEnd > nowMs;
    })
    .sort(
      (left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt),
    )
    .at(0);
  return next?.id === session.id ? "next" : "scheduled";
}

/** A compact live countdown for the nearest scheduled/live weekend session. */
export function sessionCountdown(
  session: RaceSession,
  now = new Date(),
): string {
  const status = sessionDisplayStatus(session, [session], now);
  if (status === "ongoing") return "LIVE NOW";
  if (status === "delayed") return "DELAYED";

  const startsAt = Date.parse(session.startsAt);
  if (!Number.isFinite(startsAt)) return "T−—";

  const totalSeconds = Math.max(
    0,
    Math.ceil((startsAt - now.getTime()) / 1_000),
  );
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");

  if (days > 0) return `T−${String(days)}d ${pad(hours)}h`;
  if (hours > 0) return `T−${String(hours)}h ${pad(minutes)}m`;
  if (minutes > 0) return `T−${String(minutes)}m ${pad(seconds)}s`;
  return `T−${String(seconds)}s`;
}

export function nextRaceSession(
  sessions: readonly RaceSession[],
  now = new Date(),
): RaceSession | undefined {
  const nowMs = now.getTime();
  return [...sessions]
    .filter((session) => {
      if (session.status === "cancelled" || session.status === "completed") {
        return false;
      }
      const endsAt = Date.parse(session.endsAt);
      return !Number.isFinite(endsAt) || endsAt >= nowMs;
    })
    .sort(
      (left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt),
    )
    .at(0);
}

function formatInTimezone(
  date: Date,
  timeZone: string,
  locale?: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "shortOffset",
  }).format(date);
}

function formatClock(date: Date, timeZone: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "shortOffset",
  }).format(date);
}

function formatOffset(date: Date, timeZone: string, locale?: string): string {
  return (
    new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "longOffset",
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")
      ?.value.replace("UTC", "GMT") ?? "GMT"
  );
}

export function formatUserDateTime(
  startsAt: string,
  viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale?: string,
): string {
  const date = new Date(startsAt);
  if (!Number.isFinite(date.getTime())) {
    throw new Error(`Invalid session timestamp ${startsAt}.`);
  }
  return formatInTimezone(date, viewerTimezone, locale);
}

export function formatSessionTime(
  startsAt: string,
  trackTimezone: string,
  viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale?: string,
): FormattedSessionTime {
  const date = new Date(startsAt);
  if (!Number.isFinite(date.getTime())) {
    throw new Error(`Invalid session timestamp ${startsAt}.`);
  }
  return {
    viewer: formatInTimezone(date, viewerTimezone, locale),
    viewerClock: formatClock(date, viewerTimezone, locale),
    viewerOffset: formatOffset(date, viewerTimezone, locale),
    track: formatInTimezone(date, trackTimezone, locale),
    trackOffset: formatOffset(date, trackTimezone, locale),
  };
}
